import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mic, MicOff, Wand2, Sparkles, ArrowRight, Loader2, CheckCircle2, RefreshCw, UserCircle2, ShoppingBag, Boxes, Music, ImagePlus, UploadCloud } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSpeech from "@/lib/useSpeech";
import { useMode } from "@/context/ModeContext";
import { useAuth } from "@/context/AuthContext";
import { MascotBubble } from "@/components/Mascot";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CHARACTERS = [["🐱", "Cat"], ["🐶", "Dog"], ["🤖", "Robot"], ["🦄", "Unicorn"], ["🐉", "Dragon"], ["🚀", "Rocket"], ["🐸", "Frog"], ["👾", "Alien"]];
const WORLDS = [["🌌", "Space"], ["🌳", "Jungle"], ["🏰", "Castle"], ["🌊", "Ocean"], ["🍭", "Candy"], ["❄️", "Ice"], ["🏜️", "Desert"], ["🌋", "Volcano"]];
const GOALS = [["⭐", "Collect"], ["🏁", "Race"], ["🦘", "Jump"], ["🧩", "Solve"], ["🔨", "Tap fast"], ["🎯", "Shoot"]];
const DIFFS = [["easy", "Easy 😀"], ["medium", "Medium 😎"], ["hard", "Hard 🔥"]];
const DIMS = [["", "Auto ✨"], ["2d", "2D 🕹️"], ["3d", "3D 🧊"]];
const STYLES = [
  // Action & combat
  "Action", "Fighting", "Beat 'em up", "Brawler", "Martial arts", "Boxing", "Hack & slash", "Sword combat", "Ninja action",
  // Shooters
  "Shooter", "First-person shooter", "Third-person shooter", "Hero shooter", "Arena shooter", "Tactical shooter",
  "Space shooter", "Bullet hell", "Twin-stick shooter", "Battle royale", "Zombie shooter", "Sniper", "MOBA",
  // Platformers
  "Platformer", "Classic platformer", "3D platformer", "Precision platformer", "Metroidvania", "Run & gun",
  // Racing & driving
  "Racing", "Kart racer", "Arcade racing", "Driving sim", "Time trial", "Combat racing",
  // Adventure / RPG / open world
  "Adventure", "Action-adventure", "Open world", "RPG", "Action RPG", "JRPG", "Exploration", "Stealth",
  "Survival", "Roguelike", "Dungeon crawler",
  // Sandbox & simulation
  "Sandbox", "Voxel builder", "Block builder", "Building game", "City builder", "Crafting", "Simulation",
  "Life sim", "Farming sim", "Tycoon", "Management sim",
  // Strategy
  "Strategy", "Tower defense", "Real-time strategy", "Turn-based strategy", "Tactics", "Auto battler",
  // Puzzle
  "Puzzle", "Maze", "Logic puzzle", "Match-3", "Tile matching", "Block puzzle", "Physics puzzle",
  "Hidden object", "Escape room", "Sliding puzzle", "Brain teaser",
  // Card & board
  "Card game", "Memory match", "Deck builder", "Card battle", "Board game", "Solitaire", "Mahjong",
  // Trivia & educational
  "Quiz", "Trivia", "Educational", "Word game", "Math game", "Spelling", "Typing",
  // Rhythm & music
  "Rhythm", "Music game", "Dance", "Band jam",
  // Story & narrative
  "Visual novel", "Story adventure", "Interactive fiction", "Dating sim", "Mystery", "Detective", "Choose your own adventure",
  // Horror
  "Horror", "Survival horror", "Haunted house",
  // Sports
  "Sports", "Soccer", "Basketball", "Sports arcade",
  // Casual / arcade / runner
  "Endless runner", "Auto-runner", "Flappy-style", "Arcade", "Reaction game", "Whack-a-mole",
  "Idle clicker", "Hypercasual", "Cozy game", "Kids game",
  // Visual styles / themes
  "Anime", "Cyberpunk", "Fantasy", "Sci-fi", "Pixel art", "Retro 8-bit", "Cartoon", "Realistic 3D",
];
const styleSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
// Featured/special game types that are otherwise hard to discover from the free-text idea box.
// [icon, name, rich starter idea, style, dimension]. Prefills the idea + locks style/dimension so
// the planner routes to the right engine (chess/party board, Clash-style base builder, …).
const SPECIAL_TYPES = [
  ["♟️", "Chess", "A classic chess board game with a smart AI opponent, legal-move highlighting and check / checkmate detection", "Board game", ""],
  ["🎲", "Party game", "A 4-player party board game on a looping board with dice, coins, stars and quick minigames between rounds", "Board game", ""],
  ["🏰", "Base builder", "A Clash-of-Clans style base builder: build and upgrade a village with a town hall, gold mines, cannons and archer towers, a spell forge for lightning and rage spells, train barbarians and giants, raid rival enemy villages for loot, defend against attacks and climb a trophy ladder", "Building game", "3d"],
  ["🃏", "Card battle", "A strategic card battle game where you build a deck and duel AI opponents for the win", "Card battle", ""],
  ["⚔️", "Hero brawler", "A 3D hero brawler where an armored warrior fights escalating waves of enemies with combos, dodges and power-ups", "Brawler", "3d"],
  ["🏎️", "Kart racer", "A colorful 3D kart racing game with drift boosts, item pickups and looping tracks", "Kart racer", "3d"],
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CreateGame() {
  const navigate = useNavigate();
  const { isKid } = useMode();
  const { user } = useAuth();
  const kidView = isKid || user?.role === "kid";

  const [idea, setIdea] = useState("");
  const [opts, setOpts] = useState({ character: "", world: "", goal: "", difficulty: "easy", genre: "", template: "", art_style: "", dimension: "", style: "", world_scale: "" });
  const [step, setStep] = useState("idea"); // idea | planning | review | building
  const [result, setResult] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignmentId, setAssignmentId] = useState("");
  const [classInfo, setClassInfo] = useState(null);
  const [myChars, setMyChars] = useState([]);
  const [selectedChar, setSelectedChar] = useState(null);
  const [modelStyle, setModelStyle] = useState("realistic"); // realistic | sculpture (Meshy 3D look)
  const [aaaMode, setAaaMode] = useState(true); // auto-generate real 3D models+skybox / 2D sprite art
  const [soundTheme, setSoundTheme] = useState("auto"); // auto | retro | fantasy | scifi | cute | action
  const [voiceActing, setVoiceActing] = useState(false); // generate real spoken voice-over for dialogue
  const [created, setCreated] = useState(null); // freshly-created game (for the AAA build progress panel)
  const [genAssets, setGenAssets] = useState([]); // live asset build rows
  const [genDone, setGenDone] = useState(false);
  const [engineHint, setEngineHint] = useState(null); // which underlying engine the picked style maps to
  const [trending, setTrending] = useState([]); // most-created genres this week (social proof)
  const [importing, setImporting] = useState(false); // Universal Import: image → game idea
  const importRef = useRef(null);
  const buildAfterPlanRef = useRef(false); // one-tap instant: auto-build once the plan is ready
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Deep-link support: /app/create?idea=...&style=... (used by the Discover "Featured genres" row).
    const qIdea = searchParams.get("idea");
    const qStyle = searchParams.get("style");
    if (qIdea) setIdea(qIdea);
    if (qStyle) setOpts((o) => ({ ...o, style: qStyle }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/characters").then((r) => setMyChars(r.data || [])).catch(() => {});
    api.get("/create/trending-genres").then((r) => setTrending(r.data?.genres || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "student") {
      api.get("/school/my").then((r) => { setAssignments(r.data.assignments || []); setClassInfo(r.data.class); }).catch(() => {});
    }
  }, [user]);

  const { listening, supported, start, stop } = useSpeech((text) => setIdea((p) => (p ? p + " " : "") + text));
  const set = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  // Live "matches the X engine" hint for the picked style/genre (debounced, no LLM).
  useEffect(() => {
    const s = (opts.style || "").trim();
    if (!s) { setEngineHint(null); return; }
    const t = setTimeout(() => {
      api.get("/create/engine-hint", { params: { style: s, dimension: opts.dimension || "" } })
        .then((r) => setEngineHint(r.data))
        .catch(() => setEngineHint(null));
    }, 300);
    return () => clearTimeout(t);
  }, [opts.style, opts.dimension]);


  const buildIdea = () => {
    let s = idea.trim();
    if (kidView) {
      const parts = [];
      if (opts.character) parts.push(`a ${opts.character}`);
      if (opts.goal) parts.push(`that will ${opts.goal.toLowerCase()}`);
      if (opts.world) parts.push(`in a ${opts.world.toLowerCase()} world`);
      if (parts.length) s = (s ? s + ". " : "") + `Make a game with ${parts.join(" ")}.`;
    }
    if (selectedChar) {
      const desc = selectedChar.appearance || selectedChar.traits || "";
      s = (s ? s + ". " : "") + `Star my character "${selectedChar.name}"${desc ? ` — ${desc}` : ""} as the main hero.`;
    }
    return s;
  };

  const generate = async (ideaOverride) => {
    const ideaText = typeof ideaOverride === "string" ? ideaOverride : buildIdea();
    if (!ideaText || ideaText.length < 3) { toast.error("Tell Studio your game idea first!"); return; }
    setStep("planning");
    try {
      const { data } = await api.post("/ai/plan", { idea: ideaText, options: opts });
      setResult(data);
      setStep("review");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      buildAfterPlanRef.current = false;
      setStep("idea");
    }
  };

  const surprise = () => {
    const rc = CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];
    const rw = WORLDS[Math.floor(Math.random() * WORLDS.length)];
    const rg = GOALS[Math.floor(Math.random() * GOALS.length)];
    setOpts((o) => ({ ...o, character: rc[1], world: rw[1], goal: rg[1], difficulty: "easy" }));
    generate(`Make a fun, friendly game with a ${rc[1]} that will ${rg[1].toLowerCase()} in a ${rw[1].toLowerCase()} world.`);
  };

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file."); return; }
    setImporting(true);
    toast.message("🖼️ Reading your image…");
    try {
      const blob = await resizeImageFile(f).catch(() => f);
      const fd = new FormData();
      fd.append("file", blob, "import.jpg");
      const { data } = await api.post("/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      if (data?.idea) {
        setIdea(data.idea);
        if (data.genre) setOpts((o) => ({ ...o, genre: data.genre }));
        toast.success("✨ Turned your image into a game — building it!");
        generate(data.idea);
      } else {
        toast.error("Couldn't turn that image into a game — try another.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't import that image.");
    } finally { setImporting(false); }
  };

  // Fill a random genre + a matching idea prompt (does NOT auto-generate — lets you tweak first
  // and see which engine it maps to). Great low-friction start for non-writers.
  const surpriseGenre = () => {
    const themes = new Set(["Anime", "Cyberpunk", "Fantasy", "Sci-fi", "Pixel art", "Retro 8-bit", "Cartoon", "Realistic 3D"]);
    const pool = STYLES.filter((s) => !themes.has(s));
    const g = pool[Math.floor(Math.random() * pool.length)];
    const c = CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];
    const w = WORLDS[Math.floor(Math.random() * WORLDS.length)];
    const gl = g.toLowerCase(), cl = c[1].toLowerCase(), wl = w[1].toLowerCase();
    const ideas = [
      `A ${gl} game starring a ${cl} in a ${wl} world`,
      `A ${wl}-themed ${gl} where you play as a brave ${cl}`,
      `A ${cl} hero stars in a ${wl} ${gl} adventure`,
    ];
    const newIdea = ideas[Math.floor(Math.random() * ideas.length)];
    setIdea(newIdea);
    setOpts((o) => ({ ...o, style: g, dimension: "" }));
    toast.success(`🎲 How about a ${g}? Tweak the idea, then hit Generate!`);
  };

  // One-tap instant game — pick a random genre + idea and immediately plan AND build it
  // (no review step). The AAA build panel then shows assets coming to life. Great for kids.
  const surpriseInstant = () => {
    const themes = new Set(["Anime", "Cyberpunk", "Fantasy", "Sci-fi", "Pixel art", "Retro 8-bit", "Cartoon", "Realistic 3D"]);
    const pool = STYLES.filter((s) => !themes.has(s));
    const g = pool[Math.floor(Math.random() * pool.length)];
    const c = CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];
    const w = WORLDS[Math.floor(Math.random() * WORLDS.length)];
    const newIdea = `A ${g.toLowerCase()} game starring a ${c[1].toLowerCase()} in a ${w[1].toLowerCase()} world`;
    setIdea(newIdea);
    setOpts((o) => ({ ...o, style: g, dimension: "" }));
    buildAfterPlanRef.current = true;
    toast.success(`🎲 Making a ${g} for you — sit tight!`);
    generate(newIdea);
  };

  // Trending-genre chip → prefill a starter idea + lock the style (does not auto-build).
  const pickTrending = (genre) => {
    const c = CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];
    const w = WORLDS[Math.floor(Math.random() * WORLDS.length)];
    setIdea(`A ${genre.toLowerCase()} game starring a ${c[1].toLowerCase()} in a ${w[1].toLowerCase()} world`);
    setOpts((o) => ({ ...o, style: genre, dimension: "" }));
    toast.success(`🔥 ${genre} is trending this week — here's a starter idea!`);
  };

  // Featured game-type chip → prefill a rich starter idea + lock style/dimension so the planner
  // routes to the right special engine (chess/party/base-builder/…). Does not auto-build.
  const pickSpecialType = (t) => {
    const [, name, ideaText, style, dim] = t;
    setIdea(ideaText);
    setSelectedChar(null);
    setOpts((o) => ({ ...o, style, dimension: dim }));
    toast.success(`✨ ${name} loaded — tweak the idea, then hit Generate!`);
  };

  // First-run onboarding deep-link: /app/create?surprise=1 kicks off an instant build.
  const surpriseFiredRef = useRef(false);
  useEffect(() => {
    if (searchParams.get("surprise") === "1" && !surpriseFiredRef.current && step === "idea") {
      surpriseFiredRef.current = true;
      surpriseInstant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const build = async () => {
    setStep("building");
    const _cfg = result?.config || {};
    const _hasDialogue = _cfg.template === "visualnovel" || (Array.isArray(_cfg.npcs) && _cfg.npcs.some((n) => (n.dialogue || []).length > 0));
    try {
      const { data } = await api.post("/games", {
        title: result.plan.title, idea: buildIdea(), plan: result.plan, config: result.config,
        assignment_id: assignmentId || null, art_style_3d: modelStyle, aaa_mode: aaaMode, sound_theme: soundTheme,
        voice_acting: voiceActing && _hasDialogue,
      });
      api.post("/quests/track", { event: "create" }).catch(() => {});
      // If AAA assets are being generated, show the live build panel so the creator watches
      // their world come to life; otherwise jump straight into the studio.
      if (data.model_generating || data.skybox_generating || data.art_generating || data.audio_generating || data.voice_generating) {
        setCreated(data); setGenAssets([]); setGenDone(false); setStep("generating");
      } else {
        toast.success("Game created! Opening the studio…");
        navigate(`/app/build/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStep("review");
    }
  };

  const MODEL_LABELS = { ast_player: "Hero", ast_enemy: "Enemy", ast_boss: "Boss" };
  const ART_LABELS = { player: "Hero art", collectible: "Item art", enemy: "Enemy art", boss: "Boss art" };
  const modelLabel = (aid) => {
    if (MODEL_LABELS[aid]) return MODEL_LABELS[aid];
    const m = String(aid).match(/npc_(\d+)/);
    return m ? `NPC ${parseInt(m[1], 10) + 1}` : "Character";
  };

  useEffect(() => {
    // Voice acting is the heart of a visual novel — default it on when a VN plan is generated.
    if (result?.config?.template === "visualnovel") setVoiceActing(true);
  }, [result?.config?.template]);

  useEffect(() => {
    // One-tap instant flow: as soon as the plan lands, auto-build it (skip the review step).
    if (step === "review" && result && buildAfterPlanRef.current) {
      buildAfterPlanRef.current = false;
      build();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, result]);

  useEffect(() => {
    if (step !== "generating" || !created) return;
    let cancelled = false;
    const is3D = (result?.config?.dimension || "2d") === "3d";
    const poll = async () => {
      let rows = [], allDone = true;
      if (is3D) {
        try {
          const { data } = await api.get(`/bridge/models-status/${created.id}`);
          rows = (data.models || []).map((m) => ({ key: m.asset_id, label: modelLabel(m.asset_id), status: m.status, progress: m.progress || 0 }));
          if (!data.all_done) allDone = false;
        } catch {}
        try {
          const { data: g } = await api.get(`/games/${created.id}`);
          const sky = !!(g.config?.world?.skyboxUrl);
          rows.push({ key: "skybox", label: "Cinematic sky", status: sky ? "succeeded" : "processing", progress: sky ? 100 : 65 });
          if (!sky && created.skybox_generating) allDone = false;
        } catch {}
      } else {
        try {
          const { data } = await api.get(`/games/${created.id}/art-status`);
          rows = (data.targets || []).map((t) => ({ key: t.target, label: ART_LABELS[t.target] || (String(t.target).startsWith("npc:") ? `NPC ${parseInt(String(t.target).split(":")[1], 10) + 1} art` : t.target), status: t.status === "done" ? "succeeded" : (t.status === "failed" ? "failed" : "processing"), progress: t.status === "done" ? 100 : 55 }));
          if (!data.done) allDone = false;
        } catch {}
      }
      // Soundtrack (every game) — themed AI sound pack composing in the background.
      try {
        const { data: a } = await api.get(`/audio/pack-status/${created.id}`);
        if (a && (a.audio_generating || a.done || (a.events && a.events.length))) {
          const dc = (a.events || []).filter((e) => e.status === "done").length;
          const tot = (a.events || []).length || 6;
          rows.push({ key: "soundtrack", label: "Soundtrack · BGM + SFX", status: a.done ? "succeeded" : "processing", progress: a.done ? 100 : Math.max(20, Math.round((dc / tot) * 100)) });
          if (!a.done) allDone = false;
        }
      } catch {}
      // Voice acting (opt-in) — spoken dialogue lines being generated in the background.
      if (created.voice_generating) {
        try {
          const { data: v } = await api.get(`/audio/voiceover-status/${created.id}`);
          if (v && (v.voice_generating || v.done)) {
            rows.push({ key: "voiceover", label: "Voice acting · spoken dialogue", status: v.done ? (v.status === "failed" ? "failed" : "succeeded") : "processing", progress: v.done ? 100 : 55 });
            if (!v.done) allDone = false;
          }
        } catch {}
      }
      if (!cancelled) { setGenAssets(rows); if (allDone && rows.length) setGenDone(true); }
      return allDone && rows.length;
    };
    poll();
    const t = setInterval(async () => { if (cancelled) return; const d = await poll(); if (d) clearInterval(t); }, 5000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, created]);

  // ---------- AAA BUILD PROGRESS ----------
  if (step === "generating" && created) {
    const total = genAssets.length || 1;
    const doneCount = genAssets.filter((a) => a.status === "succeeded").length;
    return (
      <div className="min-h-[80vh] grid place-items-center p-6" data-testid="aaa-build-panel">
        <div className="w-full max-w-lg animate-rise">
          <div className="text-center mb-6">
            <div className="relative inline-block mb-4">
              <div className="absolute -inset-6 bg-amber-400/20 blur-2xl rounded-full animate-pulse-glow" />
              <img src="/brand/gv-mascot.png" alt="" className="w-24 h-24 relative animate-float" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-1">{genDone ? "Your world is ready to play! 🎉" : "Bringing your world to life…"}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-sm">
              {genDone ? "Every asset is sculpted and painted. Step into the studio." : "Sculpting characters and painting the world. This runs in the background — you can enter anytime."}
            </p>
          </div>
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 space-y-3 mb-5">
            {genAssets.length === 0 && <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Warming up the AI studio…</div>}
            {genAssets.map((a) => (
              <div key={a.key} className="flex items-center gap-3" data-testid={`gen-asset-${a.key}`}>
                <div className="w-6 shrink-0">
                  {a.status === "succeeded" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    : a.status === "failed" ? <span className="text-amber-500 text-sm">⚠️</span>
                    : <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--primary))]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate">{a.label}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{a.status === "succeeded" ? "Ready" : a.status === "failed" ? "Skipped" : "Building…"}</span>
                  </div>
                  <div className="h-1.5 mt-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-pink-500 transition-all duration-700" style={{ width: `${a.status === "succeeded" ? 100 : a.progress}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button onClick={() => navigate(`/app/build/${created.id}`)} data-testid="enter-studio-btn" size="lg"
              className={`rounded-full px-8 py-6 w-full ${genDone ? "bg-gradient-to-r from-amber-500 to-pink-500 brand-glow" : "bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"} hover:opacity-90`}>
              {genDone ? <><Sparkles className="w-5 h-5" /> Enter your AAA studio</> : <>Open studio now <ArrowRight className="w-5 h-5" /></>}
            </Button>
            {!genDone && <span className="text-xs text-[hsl(var(--muted-foreground))]">{doneCount}/{total} assets ready — the rest keep building in the studio</span>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLANNING ----------
  if (step === "planning" || step === "building") {
    return (
      <div className="min-h-[80vh] grid place-items-center p-6">
        <div className="text-center animate-rise">
          <div className="relative inline-block mb-6">
            <div className="absolute -inset-6 bg-[#3b9eff]/20 blur-2xl rounded-full animate-pulse-glow" />
            <img src="/brand/gv-mascot.png" alt="" className="w-32 h-32 relative animate-float" />
          </div>
          <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-[hsl(var(--primary))]" />
          <h2 className="font-display text-2xl font-bold mb-2">
            {step === "planning" ? "Studio is dreaming up your game…" : "Building your game…"}
          </h2>
          <p className="text-[hsl(var(--muted-foreground))]">This takes just a few seconds. ✨</p>
        </div>
      </div>
    );
  }

  // ---------- REVIEW PLAN ----------
  if (step === "review" && result) {
    const p = result.plan, c = result.config;
    return (
      <div className="max-w-4xl mx-auto p-5 lg:p-8 animate-rise">
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Here's your game plan!</h1>
        </div>

        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 mb-5">
          <div className="flex items-start gap-4">
            <div className="text-5xl">{c.player?.emoji}</div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-bold">{p.title}</h2>
              <p className="text-[hsl(var(--muted-foreground))] mt-1">{p.description}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge>{p.genre}</Badge>
                <Badge>{(c.dimension || "2d") === "3d" ? "🧊 3D" : "🕹️ 2D"}</Badge>
                {opts.style && <Badge>🎨 {opts.style}</Badge>}
                <Badge>Ages {p.target_age}</Badge>
                <Badge>{p.difficulty}</Badge>
                <Badge>Template: {c.template}</Badge>
                <Badge>🛡️ {p.safety_rating}</Badge>
              </div>
            </div>
          </div>
        </div>

        {kidView ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <PlanCard emoji={c.player?.emoji} label="Your hero" value={p.main_character} />
            <PlanCard emoji="🌍" label="World" value={p.world} />
            <PlanCard emoji={c.collectible?.emoji} label="Collect" value={p.collectibles} />
            <PlanCard emoji={c.enemy?.emoji} label="Watch out for" value={p.enemies} />
            <PlanCard emoji="🏆" label="To win" value={p.win_condition} />
            <PlanCard emoji="🎮" label="Controls" value={p.controls} />
          </div>
        ) : (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden mb-6">
            {[
              ["Gameplay loop", p.gameplay_loop], ["Objective", p.objective], ["Controls", p.controls],
              ["Enemies / obstacles", p.enemies], ["Collectibles", p.collectibles],
              ["Win condition", p.win_condition], ["Lose condition", p.lose_condition],
              ["Visual style", p.visual_style], ["Sound direction", p.sound_direction],
            ].map(([k, v], i) => (
              <div key={k} className={`grid grid-cols-3 gap-3 px-5 py-3 text-sm ${i % 2 ? "bg-[hsl(var(--secondary))]/40" : ""}`}>
                <div className="font-semibold text-[hsl(var(--muted-foreground))]">{k}</div>
                <div className="col-span-2">{v}</div>
              </div>
            ))}
          </div>
        )}

        {(c.dimension || "2d") === "3d" && (
          <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/10 to-transparent p-4 mb-6" data-testid="model-style-toggle">
            <div className="flex items-center gap-2 mb-1 text-sm font-bold"><Boxes className="w-4 h-4 text-[hsl(var(--primary))]" /> 3D character style</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Pick the look for your real 3D AI models (hero, enemies, boss & friends). You can change it later in the builder.</p>
            <div className="grid grid-cols-2 gap-2 max-w-md">
              {[["realistic", "Realistic", "Detailed, textured & lifelike"], ["sculpture", "Stylized", "Clean sculpted, clay-like look"]].map(([v, label, desc]) => (
                <button key={v} type="button" onClick={() => setModelStyle(v)} data-testid={`model-style-${v}`}
                  className={`text-left rounded-xl border p-3 transition-all ${modelStyle === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
                  <div className="font-semibold text-sm flex items-center gap-1.5">{modelStyle === v && <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />}{label}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-400/10 to-transparent p-4 mb-6" data-testid="aaa-mode-toggle">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-bold"><Sparkles className="w-4 h-4 text-amber-500" /> AAA mode</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {(c.dimension || "2d") === "3d"
                  ? "Auto-sculpt real 3D character models + paint a cinematic AI skybox so your world looks premium instantly. (Uses a few AI credits · ready in a couple of minutes.)"
                  : "Auto-draw real AI sprite art for your whole cast instead of plain emojis. (Uses a few AI credits · ready in ~1 minute.)"}
              </p>
            </div>
            <button type="button" role="switch" aria-checked={aaaMode} onClick={() => setAaaMode((v) => !v)} data-testid="aaa-mode-switch"
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${aaaMode ? "bg-amber-500" : "bg-[hsl(var(--muted))]"}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${aaaMode ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          {!aaaMode && <p className="text-[11px] text-amber-600 mt-2" data-testid="aaa-mode-off-note">Fast build — your cast will use emoji/placeholder art. You can generate real art anytime in the builder.</p>}
        </div>

        <div className="rounded-2xl border border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-400/10 to-transparent p-4 mb-6" data-testid="sound-theme-toggle">
          <div className="flex items-center gap-2 text-sm font-bold"><Music className="w-4 h-4 text-fuchsia-500" /> Soundtrack style</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 mb-3">Every game ships with an AI-composed soundtrack (music + sound effects). Pick a vibe or let us match it to your game automatically.</p>
          <div className="flex flex-wrap gap-2">
            {[["auto", "✨ Auto-match"], ["retro", "🕹️ Retro"], ["fantasy", "🏰 Fantasy"], ["scifi", "🚀 Sci-Fi"], ["cute", "🍬 Cute"], ["action", "💥 Action"]].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setSoundTheme(v)} data-testid={`sound-theme-${v}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${soundTheme === v ? "border-fuchsia-500 bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {(c.template === "visualnovel" || (Array.isArray(c.npcs) && c.npcs.some((n) => (n.dialogue || []).length > 0))) && (
          <div className="rounded-2xl border border-indigo-400/40 bg-gradient-to-br from-indigo-400/10 to-transparent p-4 mb-6" data-testid="voice-acting-toggle">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-bold"><Mic className="w-4 h-4 text-indigo-500" /> Voice acting</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  Give every character a real spoken voice — the AI narrates and voices each line of dialogue with a fitting voice. {c.template === "visualnovel" ? "Brings your visual novel to life." : "Your characters will actually talk."} (Uses a few AI credits · ready in ~1 minute.)
                </p>
              </div>
              <button type="button" role="switch" aria-checked={voiceActing} onClick={() => setVoiceActing((v) => !v)} data-testid="voice-acting-switch"
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${voiceActing ? "bg-indigo-500" : "bg-[hsl(var(--muted))]"}`}>
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${voiceActing ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={build} data-testid="build-game-btn" size="lg"
            className="rounded-full px-8 py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
            <Wand2 className="w-5 h-5" /> Build my game!
          </Button>
          <Button onClick={generate} variant="outline" size="lg" className="rounded-full px-6 py-6" data-testid="regenerate-btn">
            <RefreshCw className="w-4 h-4" /> Try a different plan
          </Button>
          <Button onClick={() => setStep("idea")} variant="ghost" size="lg" className="rounded-full px-6 py-6">Edit idea</Button>
        </div>
      </div>
    );
  }

  // ---------- IDEA INPUT ----------
  return (
    <div className="max-w-3xl mx-auto p-5 lg:p-8">
      <div className="mb-6"><MascotBubble message={kidView ? "Hi! I'm Studio. Press the big mic and tell me your game idea — or pick from the buttons!" : "Describe your game idea. Be specific about the character, world and goal for the best results."} /></div>

      {!kidView && (
        <button onClick={() => navigate("/app/import")} data-testid="import-project-link"
          className="mb-5 w-full flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--secondary))]/50 p-4 text-left transition-colors">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] text-white shrink-0"><UploadCloud className="w-5 h-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold">Already have a project? Import it</span>
            <span className="block text-xs text-[hsl(var(--muted-foreground))]">Bring in GLB/glTF, sprites, audio, or a Unity/Godot/Three.js/Phaser export — get a compatibility report, then convert.</span>
          </span>
          <span className="text-[hsl(var(--primary))] text-sm font-semibold shrink-0">Import →</span>
        </button>
      )}

      {user?.role === "student" && classInfo && (
        <div className="mb-5 rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4" data-testid="student-assignment-box">
          <div className="text-sm font-bold mb-2">🎓 {classInfo.name} — build for an assignment (optional)</div>
          <Select value={assignmentId} onValueChange={setAssignmentId}>
            <SelectTrigger data-testid="assignment-select"><SelectValue placeholder="Free creation (no assignment)" /></SelectTrigger>
            <SelectContent>
              {assignments.map((a) => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {assignmentId && assignments.find((a) => a.id === assignmentId)?.description && (
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{assignments.find((a) => a.id === assignmentId).description}</p>
          )}
        </div>
      )}

      <div className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 lg:p-8 brand-glow">
        {kidView ? (
          <div className="text-center">
            <button onClick={listening ? stop : start} data-testid="kid-mic-btn"
              className={`w-28 h-28 rounded-full grid place-items-center mx-auto mb-4 text-white transition-all ${listening ? "bg-red-500 animate-pulse-glow" : "bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] hover:scale-105 brand-glow"}`}>
              {listening ? <MicOff className="w-12 h-12" /> : <Mic className="w-12 h-12" />}
            </button>
            <p className="font-display text-xl font-bold mb-1">{listening ? "Listening… speak now!" : "Tap to speak your idea"}</p>
            {!supported && <p className="text-xs text-[hsl(var(--muted-foreground))]">Voice isn't supported here — type below instead!</p>}
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              <Button onClick={surprise} variant="outline" className="rounded-full" data-testid="kid-surprise-btn">🎲 Surprise me!</Button>
              <Button onClick={surpriseInstant} className="rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-white hover:opacity-90" data-testid="kid-instant-btn">⚡ Instant game!</Button>
            </div>
          </div>
        ) : (
          <Label className="text-base font-semibold mb-2 block">Your game idea</Label>
        )}

        <Textarea data-testid="idea-input" value={idea} onChange={(e) => setIdea(e.target.value)}
          placeholder="e.g. A cat skateboards through space collecting fish and dodging aliens"
          className="mt-3 min-h-[90px] text-base resize-none" />

        {!kidView && (
          <div className="flex gap-2 mt-3">
            <Button type="button" variant="outline" size="sm" onClick={listening ? stop : start} className="rounded-full" data-testid="creator-mic-btn">
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />} {listening ? "Stop" : "Voice"}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={surpriseGenre} className="rounded-full" data-testid="surprise-genre-btn">
              🎲 Surprise me
            </Button>
            <Button type="button" size="sm" onClick={surpriseInstant} className="rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-white hover:opacity-90" data-testid="instant-game-btn">
              ⚡ Instant game
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => importRef.current?.click()} disabled={importing} className="rounded-full border-violet-500/40 text-violet-600 dark:text-violet-300" data-testid="import-image-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Import image
            </Button>
            <input ref={importRef} type="file" accept="image/*" hidden onChange={handleImportFile} data-testid="import-image-input" />
          </div>
        )}

        {/* Featured game types — discover the special engines (chess, party, base-builder…) */}
        <div className="mt-6" data-testid="special-types">
          <div className="text-sm font-bold mb-2 flex items-center gap-1.5">✨ Try a new game type <span className="font-normal text-[hsl(var(--muted-foreground))]">— board · party · base-builder &amp; more</span></div>
          <div className="flex flex-wrap gap-2">
            {SPECIAL_TYPES.map((t) => (
              <button key={t[1]} type="button" onClick={() => pickSpecialType(t)} data-testid={`special-type-${styleSlug(t[1])}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 px-3.5 py-1.5 text-sm font-semibold hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/10 transition-all">
                <span className="text-base">{t[0]}</span> {t[1]}
              </button>
            ))}
          </div>
        </div>

        {/* Trending genres — social proof of what's being created right now */}
        {trending.length > 0 && (
          <div className="mt-6" data-testid="trending-genres">
            <div className="text-sm font-bold mb-2 flex items-center gap-1.5">🔥 Trending this week <span className="font-normal text-[hsl(var(--muted-foreground))]">— what creators are making</span></div>
            <div className="flex flex-wrap gap-2">
              {trending.map((t, i) => (
                <button key={t.genre} type="button" onClick={() => pickTrending(t.genre)} data-testid={`trending-genre-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 px-3 py-1.5 text-sm font-semibold hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/10 transition-all">
                  <span className="text-base">{t.icon}</span> {t.genre}
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))] font-normal">·{t.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Game type / style selector — for everyone */}
        <div className="mt-6" data-testid="style-selector">
          <div className="text-sm font-bold mb-2">🎮 Game style <span className="font-normal text-[hsl(var(--muted-foreground))]">— pick one or type your own</span></div>
          <div className="flex flex-wrap gap-2 mb-2 max-h-36 overflow-y-auto pr-1">
            {STYLES.map((s) => (
              <button key={s} type="button" onClick={() => set("style", opts.style === s ? "" : s)} data-testid={`style-${styleSlug(s)}`}
                className={`px-3 py-1.5 rounded-full border-2 text-sm font-semibold transition-all ${opts.style === s ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
                {s}
              </button>
            ))}
          </div>
          <Input value={opts.style} onChange={(e) => set("style", e.target.value)} data-testid="style-input"
            placeholder="…or type any style: Studio Ghibli anime, cyberpunk, papercraft, voxel, cel-shaded, VR shooter…" />
          {opts.style?.trim() && engineHint && (
            <div className="mt-2 text-xs" data-testid="engine-hint">
              {engineHint.matched ? (() => {
                const showBoth = !engineHint.same && !engineHint.locked_dim;
                const e = engineHint.locked_dim ? engineHint.engines[engineHint.locked_dim] : engineHint.engines["2d"];
                const e3 = engineHint.engines["3d"];
                if (showBoth) {
                  return (
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[hsl(var(--muted-foreground))]">
                      <span className="text-[hsl(var(--primary))] font-semibold">▶ Builds with</span>
                      <span className="font-semibold text-[hsl(var(--foreground))]">{e.icon} {e.name}</span><span>(2D)</span>
                      <span>or</span>
                      <span className="font-semibold text-[hsl(var(--foreground))]">{e3.icon} {e3.name}</span><span>(3D).</span>
                      <span className="ml-0.5">Lock it:</span>
                      <button type="button" onClick={() => set("dimension", "2d")} data-testid="engine-hint-use-2d"
                        className="px-2 py-0.5 rounded-full border border-[hsl(var(--primary))]/50 font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors">Use 2D</button>
                      <button type="button" onClick={() => set("dimension", "3d")} data-testid="engine-hint-use-3d"
                        className="px-2 py-0.5 rounded-full border border-[hsl(var(--primary))]/50 font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors">Use 3D</button>
                    </div>
                  );
                }
                return (
                  <span className="text-[hsl(var(--muted-foreground))]">
                    <span className="text-[hsl(var(--primary))] font-semibold">▶ Builds with the </span>
                    <span className="font-semibold text-[hsl(var(--foreground))]">{e.icon} {e.name}</span> engine.
                    {(opts.dimension === "2d" || opts.dimension === "3d") && !engineHint.same && (
                      <button type="button" onClick={() => set("dimension", "")} data-testid="engine-hint-reset"
                        className="ml-2 underline hover:text-[hsl(var(--foreground))]">use Auto</button>
                    )}
                  </span>
                );
              })() : (
                <span className="text-[hsl(var(--muted-foreground))]">✨ Our AI will pick the best-fit engine for this style.</span>
              )}
            </div>
          )}
        </div>

        {/* World scale — how big the 3D world feels */}
        <div className="mt-6" data-testid="worldscale-selector">
          <div className="text-sm font-bold mb-2">🌍 World scale <span className="font-normal text-[hsl(var(--muted-foreground))]">— how big the 3D world feels</span></div>
          <div className="flex gap-2 max-w-md">
            {[["", "Compact"], ["large", "Large 🌄"], ["huge", "Huge 🌍"]].map(([v, l]) => (
              <button key={v || "compact"} type="button" onClick={() => set("world_scale", v)} data-testid={`worldscale-${v || "compact"}`}
                className={`flex-1 py-2 rounded-xl font-bold border-2 text-sm transition-all ${opts.world_scale === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>{l}</button>
            ))}
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Applies to 3D games — Huge worlds stitch several regions together to explore.</p>
        </div>

        {/* Use one of your own characters (incl. Marketplace purchases) */}
        <div className="mt-6" data-testid="char-picker">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold flex items-center gap-1.5"><UserCircle2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Star your own character <span className="font-normal text-[hsl(var(--muted-foreground))]">— from your library or Marketplace</span></div>
            <button type="button" onClick={() => navigate("/app/marketplace")} className="text-xs text-[hsl(var(--primary))] font-semibold flex items-center gap-1" data-testid="char-picker-market"><ShoppingBag className="w-3.5 h-3.5" /> Get more</button>
          </div>
          {myChars.length === 0 ? (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">No characters yet — make one in Character Studio or buy from the Marketplace, then star it in your game.</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {myChars.map((c) => (
                <button key={c.id} type="button" onClick={() => setSelectedChar(selectedChar?.id === c.id ? null : c)} data-testid={`char-pick-${c.id}`}
                  className={`shrink-0 w-24 rounded-xl border-2 p-2 text-center transition-all ${selectedChar?.id === c.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
                  {c.image ? <img src={`${BACKEND}${c.image}`} alt={c.name} className="w-12 h-12 mx-auto rounded-lg object-cover mb-1" loading="lazy" /> : <div className="text-3xl mb-1">{c.emoji}</div>}
                  <div className="text-[11px] font-semibold truncate">{c.name}</div>
                </button>
              ))}
            </div>
          )}
          {selectedChar && <p className="text-xs text-[hsl(var(--primary))] mt-1 font-semibold" data-testid="char-pick-selected">★ {selectedChar.name} will star in your game.</p>}
        </div>

        {kidView ? (
          <div className="mt-6 space-y-5">
            <PickRow label="Pick a hero" items={CHARACTERS} value={opts.character} onPick={(v) => set("character", v)} />
            <PickRow label="Pick a world" items={WORLDS} value={opts.world} onPick={(v) => set("world", v)} />
            <PickRow label="What's the goal?" items={GOALS} value={opts.goal} onPick={(v) => set("goal", v)} />
            <div>
              <div className="text-sm font-bold mb-2">How hard?</div>
              <div className="flex gap-2">
                {DIFFS.map(([v, l]) => (
                  <button key={v} onClick={() => set("difficulty", v)} data-testid={`diff-${v}`}
                    className={`flex-1 py-3 rounded-xl font-bold border-2 transition-all ${opts.difficulty === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))]"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold mb-2">2D or 3D?</div>
              <div className="flex gap-2">
                {DIMS.map(([v, l]) => (
                  <button key={v || "auto"} onClick={() => set("dimension", v)} data-testid={`dim-${v || "auto"}`}
                    className={`flex-1 py-3 rounded-xl font-bold border-2 transition-all ${opts.dimension === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))]"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <Field label="Difficulty">
              <Select value={opts.difficulty} onValueChange={(v) => set("difficulty", v)}>
                <SelectTrigger data-testid="creator-difficulty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DIFFS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Dimension">
              <Select value={opts.dimension || "auto"} onValueChange={(v) => set("dimension", v === "auto" ? "" : v)}>
                <SelectTrigger data-testid="creator-dimension"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto ✨ (let Studio decide)</SelectItem>
                  <SelectItem value="2d">2D 🕹️</SelectItem>
                  <SelectItem value="3d">3D 🧊</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Art style (optional)">
              <Input value={opts.art_style} onChange={(e) => set("art_style", e.target.value)} placeholder="pixel art, cartoon, neon…" data-testid="creator-artstyle" />
            </Field>
          </div>
        )}

        <Button onClick={generate} data-testid="generate-plan-btn" size="lg"
          className="w-full mt-7 rounded-full py-6 text-base bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
          <Sparkles className="w-5 h-5" /> {kidView ? "Make my game plan!" : "Generate game plan"} <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}

function resizeImageFile(file, max = 1280, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > max || height > max) {
        const s = max / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const cv = document.createElement("canvas");
      cv.width = width; cv.height = height;
      cv.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      cv.toBlob((b) => (b ? resolve(b) : reject(new Error("resize failed"))), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });
}

function PickRow({ label, items, value, onPick }) {
  return (
    <div>
      <div className="text-sm font-bold mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map(([emoji, name]) => (
          <button key={name} onClick={() => onPick(value === name ? "" : name)} data-testid={`pick-${name}`}
            className={`px-3 py-2 rounded-xl border-2 flex items-center gap-1.5 font-semibold text-sm transition-all ${value === name ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
            <span className="text-xl">{emoji}</span> {name}
          </button>
        ))}
      </div>
    </div>
  );
}

const Badge = ({ children }) => (
  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--secondary))]">{children}</span>
);
const PlanCard = ({ emoji, label, value }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
    <div className="text-3xl mb-2">{emoji}</div>
    <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] font-bold">{label}</div>
    <div className="text-sm font-medium mt-0.5 line-clamp-3">{value}</div>
  </div>
);
const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block text-sm">{label}</Label>{children}</div>
);
